import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { AppRole } from "@/types/database";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/services/LoggingService";

export interface UserProfileWithRole {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  is_accredited: boolean;
  is_active: boolean;
  avatar_url: string | null;
  persona?: 'investor' | 'advisor' | 'asset_manager' | null;
  default_currency?: string | null;
}

export function useProfile(user: User | null) {
  const queryClient = useQueryClient();

  const profileQuery = useQuery({
    queryKey: ["profile-data", user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error("Authentication required");
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        // Handle missing profile gracefully - create basic profile structure
        if ((error as any).code === 'PGRST116') {
          logger.info('useProfile', 'Profile not found, creating basic profile', { userId: user.id });
          return {
            id: user.id,
            first_name: null,
            last_name: null,
            email: user.email,
            is_accredited: false,
            is_active: true,
            avatar_url: null,
            persona: null,
            default_currency: null,
          };
        }
        
        // Log error but don't spam user with technical details
        logger.warn('useProfile', 'Profile fetch failed, using fallback', { 
          error: error.message, 
          userId: user.id 
        });
        
        // Return fallback profile instead of throwing
        return {
          id: user.id,
          first_name: null,
          last_name: null,
          email: user.email,
          is_accredited: false,
          is_active: true,
          avatar_url: null,
          persona: null,
          default_currency: null,
        };
      }

      return data as UserProfileWithRole & { persona?: 'investor' | 'advisor' | 'asset_manager' | null };
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
    retry: (failureCount, error) => {
      // Don't retry on auth errors or missing profiles
      if (error?.message?.includes('Authentication') || (error as any)?.code === 'PGRST116') {
        return false;
      }
      return failureCount < 2;
    }
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (updates: Partial<UserProfileWithRole>) => {
      console.log("[Profile] Attempting profile update:", {
        userId: user?.id,
        updates,
        timestamp: new Date().toISOString()
      });

      if (!user?.id) throw new Error("No user ID");

      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        // Reduce console spam - only log critical errors
        logger.error('useProfile', 'Profile update failed', {
          error: error.message,
          userId: user.id
        });
        throw new Error('Failed to update profile. Please try again.');
      }

      logger.info('useProfile', 'Profile updated successfully', {
        userId: user.id,
        updateCount: Object.keys(updates).length
      });

      return data;
    },
    onMutate: async (updates) => {
      console.log("[Profile] Starting optimistic update:", {
        updates,
        timestamp: new Date().toISOString()
      });

      if (!user?.id) return;

      await queryClient.cancelQueries({ queryKey: ["profile-data", user.id] });
      const previousProfile = queryClient.getQueryData<UserProfileWithRole>(["profile-data", user.id]);

      queryClient.setQueryData<UserProfileWithRole>(["profile-data", user.id], old => ({
        ...(old || {}),
        ...updates,
      } as UserProfileWithRole));

      return { previousProfile };
    },
    onError: (error, _, context) => {
      console.error("[Profile] Error during profile update:", {
        error: error.message,
        userId: user?.id,
        timestamp: new Date().toISOString()
      });

      if (user?.id && context?.previousProfile) {
        queryClient.setQueryData(["profile-data", user.id], context.previousProfile);
      }
      toast.error(error.message || "Failed to update profile");
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["profile-data", user?.id], data);
      toast.success("Profile updated successfully");
    }
  });

  return {
    profile: profileQuery.data,
    isLoading: profileQuery.isLoading,
    error: profileQuery.error,
    updateProfile: updateProfileMutation.mutateAsync
  };
}
